"use client"

import { useState } from "react"
import { mutate } from "swr"
import { AlertTriangle, Check, Copy, Loader2, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ApiKeyService } from "@/services/api-key.service"

interface CreateApiKeyDialogProps {
  trigger?: React.ReactNode
  onSuccess?: () => void
}

export function CreateApiKeyDialog({ trigger, onSuccess }: CreateApiKeyDialogProps) {
  const [open, setOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [newKeyName, setNewKeyName] = useState("")
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle")
  const [error, setError] = useState<string | null>(null)

  const resetDialog = () => {
    setIsCreating(false)
    setNewKeyName("")
    setCreatedKey(null)
    setCopyStatus("idle")
    setError(null)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isCreating) return

    setOpen(nextOpen)

    if (!nextOpen) {
      resetDialog()
    }
  }

  const handleCreate = async () => {
    if (!newKeyName.trim()) return

    setIsCreating(true)
    setError(null)

    try {
      const rawKey = await ApiKeyService.create(newKeyName.trim())

      setNewKeyName("")
      setCreatedKey(rawKey)
      setCopyStatus("idle")
      // Mutate global SWR key for API keys
      mutate("api-keys")
      if (onSuccess) onSuccess()
    } catch {
      setError("Failed to create API key. Please try again.")
    } finally {
      setIsCreating(false)
    }
  }

  const handleCopy = async () => {
    if (!createdKey) return

    try {
      await navigator.clipboard.writeText(createdKey)
      setCopyStatus("copied")
    } catch {
      setCopyStatus("error")
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600">
            <Plus className="mr-2 h-4 w-4" /> Create New Key
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{createdKey ? "API Key Created" : "Create API Key"}</DialogTitle>
          <DialogDescription>
            {createdKey
              ? "Copy your new API key before closing this dialog."
              : "Create a new API key to access the Youzu API."}
          </DialogDescription>
        </DialogHeader>
        {createdKey ? (
          <div className="space-y-4 py-4">
            <Alert className="border-amber-300 bg-amber-50 text-amber-950">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>This key is shown only once</AlertTitle>
              <AlertDescription>
                Copy it now. You cannot recover it after closing this dialog.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label htmlFor="created-api-key">Your API Key</Label>
              <div className="flex items-start gap-2">
                <code
                  id="created-api-key"
                  className="min-w-0 flex-1 break-all rounded-md border bg-muted p-3 font-mono text-sm"
                >
                  {createdKey}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  aria-label="Copy API key"
                >
                  {copyStatus === "copied" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {copyStatus === "copied" && (
                <p className="text-sm text-green-700" role="status">
                  Copied to clipboard.
                </p>
              )}
              {copyStatus === "error" && (
                <p className="text-sm text-red-500" role="alert">
                  Could not copy automatically. Select and copy the key manually.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Key Name</Label>
              <Input
                id="name"
                placeholder="e.g. Production Server, Mobile App"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        )}
        {createdKey ? (
          <DialogFooter>
            <Button type="button" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating || !newKeyName.trim()}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Key
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
